import { useInfiniteQuery } from '@tanstack/react-query';
import { getOffsetPageParam, unwrap } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { SitesFilters } from './useSitesFilters';

export const sitesKeys = {
  all: ['sites'] as const,
  lists: () => [...sitesKeys.all, 'list'] as const,
  list: (filters: SitesFilters) => [...sitesKeys.lists(), filters] as const,
  details: () => [...sitesKeys.all, 'detail'] as const,
  detail: (id: number) => [...sitesKeys.details(), id] as const,
  burndown: (siteId: number) => [...sitesKeys.all, 'burndown', siteId] as const,
};

const ITEMS_PER_PAGE = 20;

/**
 * Infinite query hook to fetch sites with search and pagination via v2 API.
 */
export function useSites(filters: SitesFilters) {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  return useInfiniteQuery({
    queryKey: sitesKeys.list(filters),
    queryFn: async ({ pageParam = 0, signal }) =>
      unwrap(
        await api.legacy.GET('/api/v2/sites', {
          params: {
            query: {
              offset: pageParam as number,
              take: ITEMS_PER_PAGE,
              search: filters.search || undefined,
            },
          },
          signal,
        })
      ),
    getNextPageParam: getOffsetPageParam,
    initialPageParam: 0,
    enabled: isAuthenticated,
    staleTime: 30000,
    placeholderData: previousData => previousData,
  });
}
